import { createContext, useContext, useEffect, useState } from "react";

export const TRANSLATIONS = {
  fr: {
    nav: {
      links: [
        { label: "La Villa", id: "villa-chapitre" },
        { label: "Photos", id: "galerie" },
        { label: "Plan", id: "plan" },
        { label: "Avis", id: "avis" },
        { label: "Localisation", id: "localisation" },
      ],
      reserve: "Réserver",
      menu: "Menu",
      close: "Fermer",
    },
    survey: {
      eyebrow: "Une petite question",
      question: "Comment nous avez-vous connus ?",
      options: [
        { key: "instagram", label: "Instagram" },
        { key: "google", label: "Google" },
        { key: "amis", label: "Ami(e)s" },
        { key: "autres", label: "Autres" },
      ],
      send: "Envoyer",
      thanks: "Merci pour votre réponse !",
    },
    hero: {
      eyebrow: "Corse du Sud · Location de villa",
      tagline: "Une parenthèse d'exception au cœur du domaine de Cala Rossa.",
      cta: "Demander les disponibilités",
      scroll: "Défiler",
      titleLines: ["Au cœur du domaine", "privé de Cala Rossa"],
    },
    manifesto: {
      eyebrow: "Bienvenue à la Villa Tutto Passa",
      titleA: "Ici, le temps",
      titleB: "ralentit.",
      intro: [
        "Passé le portail, le maquis referme la route derrière vous et seul demeure l'essentiel : la lumière du golfe de Porto-Vecchio, le parfum du myrte et des pins parasols, et le bleu profond de la mer Tyrrhénienne qui n'est jamais bien loin.",
        "La Villa Tutto Passa a été pensée comme un refuge — un lieu où l'on pose ses valises et où l'on se sent, dès les premières minutes, chez soi. Chaque espace a été conçu pour le confort, la convivialité et la douceur de vivre à la corse.",
      ],
      chapters: [
        {
          num: "01",
          title: "La villa",
          body: "À l'abri des regards, à seulement 250 m à pied d'une des plages du domaine privé et sécurisé de Cala Rossa, cette villa de standing climatisée au style ethnique chic propose une vaste pièce de vie ouverte sur l'extérieur, une cuisine entièrement équipée et un salon chaleureux muni d'une belle cheminée. Cinq chambres suites au décor de caractère, avec salle de bain et toilettes privatives, accueillent jusqu'à 10 personnes — deux d'entre elles, à l'étage, disposent d'une terrasse en bois aménagée de grands beds pour profiter des nuits étoilées sous le ciel corse. La villa est accessible aux personnes à mobilité réduite et équipée pour les nourrissons.",
        },
        {
          num: "02",
          title: "Les extérieurs",
          body: "Un espace Zen, composé de deux cocons intimistes et agrémenté d'un hamac, invite à se détendre et à buller en toute tranquillité. Sur la terrasse, une piscine vient compléter cet espace extérieur, entourée de quatre beds qui invitent à profiter du soleil et à se prélasser au bord de l'eau. Une grande table permet également de partager des repas en plein air, dans une atmosphère conviviale.\n\nDans le jardin, un espace SPA avec jacuzzi offre un moment de détente supplémentaire. Sans oublier le Carport avec chargeur électrique pouvant abriter jusqu'à quatre véhicules.",
        },
        {
          num: "03",
          title: "Les loisirs",
          body: "La villa offre de nombreux loisirs pour petits et grands :\n- boulodrome\n- table de ping-pong\n- jeux de fléchettes\n- jeux de sociétés\n\nCôté mer :\n- paddles (2)\n- canoës (3)\n- masques et tubas\n\nLes enfants disposent d'un espace dédié avec cabane et jeux en bois, niché dans les hauteurs du jardin.\nUne Fiat 500 électrique est également proposée à la location.",
        },
        {
          num: "04",
          title: "La Corse-du-Sud",
          body: "Entre le maquis odorant, les criques de sable blanc et les villages perchés de l'Alta Rocca, la Corse-du-Sud est une terre de contrastes : sauvage et raffinée, discrète et spectaculaire. Porto-Vecchio, la « cité du sel », concentre en quelques kilomètres certaines des plus belles plages d'Europe, une citadelle historique animée le soir venu et un arrière-pays montagneux à couper le souffle.",
        },
      ],
      note: "Pour agrémenter votre séjour : table de ping-pong, paddles et canoë à disposition. Une Fiat 500 électrique est proposée à la location.",
      quote: "« Le plus beau voyage n'est pas celui que l'on prépare, mais celui que l'on savoure. »",
      quoteSub: "Nous vous souhaitons un séjour à la hauteur de cette promesse.",
    },
    gallery: {
      eyebrow: "Galerie",
      title: "Chaque angle, une évasion",
      hint: "Cliquez sur une photo pour l'agrandir en plein écran.",
      cta: "Découvrir la Villa",
      more: "Afficher plus de photos",
      less: "Afficher moins de photos",
    },
    plan: {
      eyebrow: "Visite 3D",
      title: "Explorez la villa en 3D, pièce par pièce",
      hint: "Faites pivoter la maison à 360° et cliquez sur une pièce : ses photos apparaissent en diaporama.",
      dragHint: "Glissez pour tourner · molette pour zoomer",
      nightLabel: "Nuit",
      dayLabel: "Jour",
      rooms: {
        salon: "Salon & SAM",
        cuisine: "Cuisine",
        chambre1: "Ch. noire",
        chambre2: "Ch. beige",
        chambre3: "Ch. bleue (PMR)",
        chambre4: "Ch. terracota",
        chambre5: "Ch. verte",
        cointv: "Coin TV & bureau",
        sdb: "Entrée & WC",
        terrasse: "Extérieur",
        piscine: "Piscine",
        garage: "Carport",
      },
    },
    amenities: {
      eyebrow: "Équipements & prestations",
      title: "Le confort, sans compromis",
      more: "Afficher plus de services",
      less: "Afficher moins",
      items: [
        { icon: "Footprints", label: "Plage à 250 m à pied" },
        { icon: "Waves", label: "Piscine chauffée" },
        { icon: "Sparkles", label: "Jacuzzi & spa" },
        { icon: "Flame", label: "Cheminée" },
        { icon: "Wind", label: "Climatisation" },
        { icon: "Car", label: "Carport sécurisé" },
        { icon: "Zap", label: "Borne de recharge" },
        { icon: "CarFront", label: "Fiat 500 électrique en location" },
        { icon: "Wifi", label: "Wi-Fi fibre haut débit" },
        { icon: "BedDouble", label: "5 suites avec SDB privatives" },
        { icon: "CircleDot", label: "Pétanque" },
        { icon: "Target", label: "Fléchettes" },
        { icon: "Racket", label: "Ping-pong" },
        { icon: "Sailboat", label: "Paddles & canoë" },
        { icon: "Users", label: "Jusqu'à 10 voyageurs" },
        { icon: "Accessibility", label: "Accessible PMR" },
        { icon: "Baby", label: "Équipements bébé" },
      ],
    },
    reviews: {
      eyebrow: "Avis clients",
      title: "Ils ont séjourné ici",
      googleLabel: "· Avis Google",
      badge: "Avis Google",
      allLink: "Voir tous les avis sur Google",
      readMore: "Lire la suite",
      readLess: "Réduire",
    },
    location: {
      eyebrow: "Localisation",
      title: "À 3 minutes à pied de la première plage",
      body: "Un écrin de verdure au sein du domaine privé et sécurisé de Cala Rossa, à quelques pas des plus belles plages du golfe de Porto-Vecchio.",
      mapBtn: "Ouvrir dans Google Maps",
      facts: [
        { icon: "Waves", label: "Plage privée du domaine", value: "250 m à pied" },
        { icon: "Sailboat", label: "Centre de Porto-Vecchio", value: "10 min" },
        { icon: "MapPin", label: "Commerces", value: "5 min" },
        { icon: "Plane", label: "Aéroport Figari", value: "40 min" },
      ],
    },
    map: {
      eyebrow: "La Corse",
      title: "Une île, une adresse",
      body: "Sur la côte sud-est de l'île de Beauté, la villa est nichée au cœur du domaine privé de Cala Rossa, à quelques minutes de Porto-Vecchio.",
    },
    contact: {
      eyebrow: "Contact",
      titleA: "Réservez votre",
      titleB: "séjour",
      body: "Indiquez-nous vos dates d'arrivée et de départ, ainsi que le nombre de voyageurs. Les périodes déjà réservées apparaissent grisées dans le calendrier. Nous revenons vers vous sous 24 h — sans paiement en ligne.",
      emailLabel: "E-mail",
      phoneLabel: "Téléphone",
      arrival: "Date d'arrivée",
      departure: "Date de départ",
      firstName: "Prénom",
      lastName: "Nom",
      email: "Adresse e-mail",
      phone: "Téléphone",
      datesPlaceholder: "Sélectionnez vos dates",
      legend: "Les dates barrées d'une croix sont déjà réservées.",
      guests: "Voyageurs",
      message: "Votre message (occasion, questions...)",
      send: "Envoyer ma demande",
      sending: "Envoi en cours...",
      successTitle: "Demande envoyée",
      successBody: "Merci ! Nous vous recontactons très vite pour organiser votre séjour à la Villa Tutto Passa.",
      another: "Envoyer une autre demande",
      errFields: "Merci de renseigner vos coordonnées.",
      errSend: "Une erreur est survenue. Réessayez ou écrivez-nous par e-mail.",
      ok: "Votre demande a bien été envoyée. Nous vous répondrons rapidement.",
      recapTitle: "Récapitulatif de votre demande",
      recapNights: "Nuits",
      recapNoDates: "Dates non précisées",
      recapEdit: "Modifier",
      recapConfirm: "Confirmer et envoyer",
    },
    instagram: {
      title: "Suivez la villa au fil des saisons",
      cta: "Suivre @villa_tuttopassa",
    },
    footer: {
      contact: "Contact",
      follow: "Suivez-nous",
      share: "Partager la villa",
      shareText: "Découvrez la Villa Tutto Passa à Cala Rossa, Corse-du-Sud 🌴",
      shareCopied: "Lien copié ! Collez-le dans votre story ou message Instagram.",
      rights: "Tous droits réservés.",
      showcase: "Site vitrine · Aucune transaction en ligne",
      owner: "Espace propriétaire",
    },
  },
  en: {
    nav: {
      links: [
        { label: "The Villa", id: "villa-chapitre" },
        { label: "Photos", id: "galerie" },
        { label: "Floor plan", id: "plan" },
        { label: "Reviews", id: "avis" },
        { label: "Location", id: "localisation" },
      ],
      reserve: "Book now",
      menu: "Menu",
      close: "Close",
    },
    survey: {
      eyebrow: "A quick question",
      question: "How did you hear about us?",
      options: [
        { key: "instagram", label: "Instagram" },
        { key: "google", label: "Google" },
        { key: "amis", label: "Friends" },
        { key: "autres", label: "Other" },
      ],
      send: "Send",
      thanks: "Thank you for your answer!",
    },
    hero: {
      eyebrow: "Southern Corsica · Villa rental",
      tagline: "An exceptional retreat in the heart of the Cala Rossa estate.",
      cta: "Check availability",
      scroll: "Scroll",
      titleLines: ["In the heart of the private", "Cala Rossa estate"],
    },
    manifesto: {
      eyebrow: "Welcome to Villa Tutto Passa",
      titleA: "Here, time",
      titleB: "slows down.",
      intro: [
        "Once through the gate, the maquis closes the road behind you and only the essential remains: the light of the Gulf of Porto-Vecchio, the scent of myrtle and umbrella pines, and the deep blue of the Tyrrhenian Sea, never far away.",
        "Villa Tutto Passa was designed as a refuge — a place where you set down your bags and feel at home within minutes. Every space has been created for comfort, conviviality and the gentle Corsican art of living.",
      ],
      chapters: [
        {
          num: "01",
          title: "The villa",
          body: "Hidden from view, just 250 m on foot from one of the beaches of the private, secure Cala Rossa estate, this air-conditioned villa with its ethnic-chic style offers a vast living space opening onto the outdoors, a fully equipped kitchen and a warm lounge with a beautiful fireplace. Five elegant en-suite bedrooms, each with private bathroom and toilet, sleep up to 10 guests — two of them, upstairs, feature a wooden terrace with large daybeds for starlit nights under the Corsican sky. The villa is accessible to people with reduced mobility and fully equipped for infants.",
        },
        {
          num: "02",
          title: "The grounds",
          body: "A Zen area with two intimate cocoons and a hammock invites you to unwind and relax in complete tranquillity. On the terrace, a pool completes this outdoor space, surrounded by four sun beds inviting you to soak up the sun and lounge by the water. A large table also lets you share open-air meals in a convivial atmosphere.\n\nIn the garden, a spa area with jacuzzi offers an extra moment of relaxation. Not to mention the carport with an electric charger, sheltering up to four vehicles.",
        },
        {
          num: "03",
          title: "Leisure & activities",
          body: "The villa offers plenty of activities for young and old:\n- pétanque court\n- table tennis\n- darts\n- board games\n\nOn the water side:\n- paddleboards (2)\n- canoes (3)\n- masks and snorkels\n\nChildren have their own dedicated area with a cabin and wooden games, nestled in the upper garden.\nAn electric Fiat 500 is also available for hire.",
        },
        {
          num: "04",
          title: "Southern Corsica",
          body: "Between the fragrant maquis, white-sand coves and the hilltop villages of the Alta Rocca, Southern Corsica is a land of contrasts: wild yet refined, discreet yet spectacular. Porto-Vecchio, the “salt city”, gathers within a few kilometres some of Europe's most beautiful beaches, a historic citadel that comes alive in the evening and a breathtaking mountain hinterland.",
        },
      ],
      note: "To brighten your stay: table tennis, paddleboards and a canoe are at your disposal. An electric Fiat 500 is available for hire.",
      quote: "“The most beautiful journey is not the one you plan, but the one you savour.”",
      quoteSub: "We wish you a stay worthy of that promise.",
    },
    gallery: {
      eyebrow: "Gallery",
      title: "Every angle, an escape",
      hint: "Click any photo to view it full screen.",
      cta: "Discover the Villa",
      more: "Show more photos",
      less: "Show fewer photos",
    },
    plan: {
      eyebrow: "3D tour",
      title: "Explore the villa in 3D, room by room",
      hint: "Rotate the house 360° and click any room: its photos appear as a slideshow.",
      dragHint: "Drag to rotate · scroll to zoom",
      nightLabel: "Night",
      dayLabel: "Day",
      rooms: {
        salon: "Living & dining room",
        cuisine: "Kitchen",
        chambre1: "Black bedroom",
        chambre2: "Beige bedroom",
        chambre3: "Blue bedroom (accessible)",
        chambre4: "Terracotta bedroom",
        chambre5: "Green bedroom",
        cointv: "TV lounge & office",
        sdb: "Entrance & WC",
        terrasse: "Outdoors",
        piscine: "Pool",
        garage: "Carport",
      },
    },
    amenities: {
      eyebrow: "Amenities & services",
      title: "Comfort, without compromise",
      more: "Show more services",
      less: "Show less",
      items: [
        { icon: "Footprints", label: "Beach 250 m on foot" },
        { icon: "Waves", label: "Heated pool" },
        { icon: "Sparkles", label: "Jacuzzi & spa" },
        { icon: "Flame", label: "Fireplace" },
        { icon: "Wind", label: "Air conditioning" },
        { icon: "Car", label: "Secure carport" },
        { icon: "Zap", label: "EV charging point" },
        { icon: "CarFront", label: "Electric Fiat 500 for hire" },
        { icon: "Wifi", label: "High-speed fibre Wi-Fi" },
        { icon: "BedDouble", label: "5 en-suite bedrooms" },
        { icon: "CircleDot", label: "Pétanque" },
        { icon: "Target", label: "Darts" },
        { icon: "Racket", label: "Table tennis" },
        { icon: "Sailboat", label: "Paddleboards & canoe" },
        { icon: "Users", label: "Up to 10 guests" },
        { icon: "Accessibility", label: "Reduced-mobility access" },
        { icon: "Baby", label: "Baby equipment" },
      ],
    },
    reviews: {
      eyebrow: "Guest reviews",
      title: "They stayed here",
      googleLabel: "· Google Reviews",
      badge: "Google review",
      allLink: "See all reviews on Google",
      readMore: "Read more",
      readLess: "Show less",
    },
    location: {
      eyebrow: "Location",
      title: "A 3-minute walk from the nearest beach",
      body: "A green haven inside the private, secure Cala Rossa estate, moments from the most beautiful beaches of the Gulf of Porto-Vecchio.",
      mapBtn: "Open in Google Maps",
      facts: [
        { icon: "Waves", label: "Private estate beach", value: "250 m on foot" },
        { icon: "Sailboat", label: "Porto-Vecchio centre", value: "10 min" },
        { icon: "MapPin", label: "Shops", value: "5 min" },
        { icon: "Plane", label: "Figari airport", value: "40 min" },
      ],
    },
    map: {
      eyebrow: "Corsica",
      title: "One island, one address",
      body: "On the south-east coast of the Isle of Beauty, the villa is nestled in the heart of the private Cala Rossa estate, minutes from Porto-Vecchio.",
    },
    contact: {
      eyebrow: "Contact",
      titleA: "Book your",
      titleB: "stay",
      body: "Tell us your arrival and departure dates, along with the number of guests. Already-booked periods appear greyed out in the calendar. We reply within 24 hours — no online payment.",
      emailLabel: "E-mail",
      phoneLabel: "Phone",
      arrival: "Arrival date",
      departure: "Departure date",
      firstName: "First name",
      lastName: "Last name",
      email: "E-mail address",
      phone: "Phone number",
      datesPlaceholder: "Select your dates",
      legend: "Crossed-out dates are already booked.",
      guests: "Guests",
      message: "Your message (occasion, questions...)",
      send: "Send my request",
      sending: "Sending...",
      successTitle: "Request sent",
      successBody: "Thank you! We will get back to you very soon to arrange your stay at Villa Tutto Passa.",
      another: "Send another request",
      errFields: "Please fill in your contact details.",
      errSend: "Something went wrong. Please try again or e-mail us.",
      ok: "Your request has been sent. We will reply shortly.",
      recapTitle: "Summary of your request",
      recapNights: "Nights",
      recapNoDates: "Dates not specified",
      recapEdit: "Edit",
      recapConfirm: "Confirm and send",
    },
    instagram: {
      title: "Follow the villa through the seasons",
      cta: "Follow @villa_tuttopassa",
    },
    footer: {
      contact: "Contact",
      follow: "Follow us",
      share: "Share the villa",
      shareText: "Discover Villa Tutto Passa in Cala Rossa, South Corsica 🌴",
      shareCopied: "Link copied! Paste it in your Instagram story or message.",
      rights: "All rights reserved.",
      showcase: "Showcase site · No online transactions",
      owner: "Owner area",
    },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("vtp_lang") || "fr");

  useEffect(() => {
    localStorage.setItem("vtp_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: TRANSLATIONS[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
